import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useDeleteCalendarEventMutation, useGetCalendarEventByIdQuery } from "calendar/_api/calendar-event-api";
import type { CalendarEventOccurrenceTime, CalendarEventResponse } from "calendar/_schemas/calendar-event-schema";
import { formatEventRange, formatRecurrence, getSafeExternalLink } from "calendar/calendar-event-display";
import { calendarEventColors, calendarEventKindLabels, getCalendarEventKind } from "calendar/calendar-event";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useSnackbar } from "notistack";
import { Link } from "react-router";

type CalendarEventDetailsProps = {
  eventId: string;
  occurrence?: CalendarEventOccurrenceTime;
  onEdit: (event: CalendarEventResponse) => void;
};

const EventTypeChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "eventColor",
})<{ eventColor: string }>(({ eventColor, theme }) => ({
  backgroundColor: eventColor,
  color: theme.palette.getContrastText(eventColor),
}));
const LoadingState = styled(Box)(({ theme }) => ({ display: "flex", alignItems: "center", gap: theme.spacing(1) }));
const DetailsLayout = styled(Box)(({ theme }) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(2) }));
const EventHeader = styled(Box)(({ theme }) => ({ display: "flex", justifyContent: "space-between", gap: theme.spacing(1), alignItems: "flex-start", flexWrap: "wrap" }));
const DetailActions = styled(Box)(({ theme }) => ({ display: "flex", justifyContent: "flex-end", gap: theme.spacing(1) }));
const DetailValue = styled(Typography)({ whiteSpace: "pre-wrap" });
const RelatedItemContainer = styled(Box)({ alignSelf: "flex-start" });

export default function CalendarEventDetails({ eventId, occurrence, onEdit }: Readonly<CalendarEventDetailsProps>) {
  const [deleteEvent, deleteState] = useDeleteCalendarEventMutation();
  // Once deletion starts, stop subscribing to the detail cache. The successful
  // mutation invalidates this event id; keeping the subscription alive would
  // refetch a resource that has just been deleted and produce an avoidable 404.
  const eventQuery = useGetCalendarEventByIdQuery({ eventId }, { skip: deleteState.isLoading });
  const confirm = useConfirmDialog();
  const { closeDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  if (eventQuery.isLoading) {
    return (
      <LoadingState role="status">
        <CircularProgress size={20} />
        Loading event…
      </LoadingState>
    );
  }
  if (eventQuery.isError || !eventQuery.data) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => void eventQuery.refetch()}>Retry</Button>}>
        Event details could not be loaded.
      </Alert>
    );
  }

  const event = eventQuery.data;
  const displayedEvent = occurrence ? { ...event, ...occurrence } : event;
  const kind = getCalendarEventKind(displayedEvent);
  const isEditable = event.canEdit && !event.isSourceManaged;
  const handleDelete = async () => {
    const response = await confirm({
      title: event.recurrence ? "Delete recurring series?" : "Delete event?",
      message: event.recurrence
        ? `Delete “${event.title}” and every occurrence in this series?`
        : `Delete “${event.title}”?`,
      okButtonText: event.recurrence ? "Delete series" : "Delete",
      cancelButtonText: "Cancel",
    });
    if (response !== ConfirmResponse.Ok) return;
    try {
      await deleteEvent({ eventId: event.id }).unwrap();
      closeDialog();
      enqueueSnackbar(event.recurrence ? "Recurring event series deleted." : "Calendar event deleted.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(getErrorMessageFromUnknown(error) ?? "The calendar event could not be deleted.", { variant: "error" });
    }
  };

  return (
    <DetailsLayout>
      <EventHeader>
        <Box>
          <Typography variant="h6" component="h2">{event.title}</Typography>
          <Typography color="text.secondary">{formatEventRange(displayedEvent)}</Typography>
          {displayedEvent.timeZoneId && <Typography variant="caption" color="text.secondary">Time zone: {displayedEvent.timeZoneId}</Typography>}
        </Box>
        <EventTypeChip eventColor={calendarEventColors[kind]} label={calendarEventKindLabels[kind]} />
      </EventHeader>
      <Divider />
      <Detail label="Description" value={event.description || "No description"} />
      <Detail label="Repeats" value={formatRecurrence(event.recurrence, displayedEvent.isAllDay)} />
      {event.link && <EventLink link={event.link} />}
      {(event.isSourceManaged || !event.canEdit) && (
        <Alert severity="info">This event is read-only.</Alert>
      )}
      {isEditable && (
        <DetailActions>
          <Button color="error" disabled={deleteState.isLoading} variant="outlined" onClick={() => void handleDelete()}>
            {deleteState.isLoading ? "Deleting…" : event.recurrence ? "Delete series" : "Delete"}
          </Button>
          <Button disabled={deleteState.isLoading} variant="contained" onClick={() => onEdit(event)}>
            {event.recurrence ? "Edit series" : "Edit"}
          </Button>
        </DetailActions>
      )}
    </DetailsLayout>
  );
}

function Detail({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <Box>
      <Typography variant="subtitle2">{label}</Typography>
      <DetailValue>{value}</DetailValue>
    </Box>
  );
}

function EventLink({ link }: Readonly<{ link: string }>) {
  const safeExternalLink = getSafeExternalLink(link);
  if (link.startsWith("/") && !link.startsWith("//")) {
    return <RelatedItemContainer><Button component={Link} to={link} variant="outlined">Open related item</Button></RelatedItemContainer>;
  }
  if (safeExternalLink) {
    return (
      <RelatedItemContainer>
        <Button component="a" href={safeExternalLink} target="_blank" rel="noopener noreferrer" variant="outlined">
          Open related item
        </Button>
      </RelatedItemContainer>
    );
  }
  return null;
}
